// pages/creator-panel/Channels/WhatsappGroup/WhatsappGroupChannel.js
//
// WhatsApp Group connector (Connectors -> Channels). Lets a creator bring a
// WhatsApp group's text into the chatbot via three methods:
//   1. Export (.txt) upload  2. Live QR pairing  3. Third-party provider API
// then trains it into the vector knowledge base. Mirrors the Instagram /
// SmsBot connector conventions (axiosInstance, hasPermission('channels', ...)).
import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import axiosInstance from "../../../../utils/axiosInstance";
import { hasPermission } from "../../../../utils/permissionUtils";
import {
  Box, Typography, Alert, Tabs, Tab, Button, TextField, FormControlLabel,
  Checkbox, Paper, CircularProgress, Chip, Stack, Divider,
  IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ListWhatsappGroups from "./ListWhatsappGroups";

export default function WhatsappGroupChannel() {
  const { chatbotId } = useParams();
  const params = chatbotId ? { chatbot_id: chatbotId } : {};

  // Permissions
  const [canView, setCanView] = useState(false);
  const [canModify, setCanModify] = useState(false);
  const [canDelete, setCanDelete] = useState(false);
  const [checkingPermissions, setCheckingPermissions] = useState(true);

  const [tab, setTab] = useState(0);
  const [message, setMessage] = useState(null); // { type, text }
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Export
  const [exportFile, setExportFile] = useState(null);
  const [groupName, setGroupName] = useState("");
  const [anonymize, setAnonymize] = useState(true);
  const [uploading, setUploading] = useState(false);

  // QR
  const [qrConnectionId, setQrConnectionId] = useState(null);
  const [qrImage, setQrImage] = useState(null);
  const [qrStatus, setQrStatus] = useState(null);
  const [qrLoading, setQrLoading] = useState(false);
  const qrPollRef = useRef(null);

  // Provider
  const [provider, setProvider] = useState({
    provider_type: "generic", api_base_url: "", api_key: "", instance_id: "",
  });
  const [providerSaving, setProviderSaving] = useState(false);
  const [lastConnectionId, setLastConnectionId] = useState(null);
  const [providerHelpOpen, setProviderHelpOpen] = useState(false);

  useEffect(() => {
    document.title = "WhatsApp Group | KlonIT Web Application";
  }, []);

  useEffect(() => {
    const check = async () => {
      setCheckingPermissions(true);
      try {
        setCanView(await hasPermission("channels", "view"));
        setCanModify(await hasPermission("channels", "add|edit"));
        setCanDelete(await hasPermission("channels", "delete"));
      } catch (e) {
        setCanView(false); setCanModify(false); setCanDelete(false);
      } finally {
        setCheckingPermissions(false);
      }
    };
    check();
  }, []);

  // Stop polling on unmount
  useEffect(() => () => { if (qrPollRef.current) clearInterval(qrPollRef.current); }, []);

  const notify = (type, text) => setMessage({ type, text });

  // --- Method 1: Export upload ---
  const handleUpload = async (e) => {
    e.preventDefault();
    if (!exportFile) { notify("error", "Choose a WhatsApp .txt export first."); return; }
    setUploading(true);
    setMessage(null);
    try {
      const form = new FormData();
      form.append("file", exportFile);
      if (groupName.trim()) form.append("group_name", groupName.trim());
      form.append("anonymize", anonymize ? "true" : "false");
      const res = await axiosInstance.post("/api/whatsapp-group/upload", form, {
        params, headers: { "Content-Type": "multipart/form-data" },
      });
      if (res.data.success) {
        notify("success", res.data.message);
        setExportFile(null); setGroupName("");
        setRefreshTrigger((p) => p + 1);
      } else {
        notify("error", res.data.message || "Upload failed.");
      }
    } catch (err) {
      notify("error", err.response?.data?.message || "Upload failed.");
    } finally {
      setUploading(false);
    }
  };

  // --- Method 3: QR pairing ---
  const startQr = async () => {
    setQrLoading(true);
    setMessage(null);
    setQrImage(null);
    setQrStatus(null);
    try {
      const res = await axiosInstance.post("/api/whatsapp-group/qr/create", {}, { params });
      if (res.data.success) {
        setQrConnectionId(res.data.connection_id);
        setQrImage(res.data.qr || null);
        setQrStatus(res.data.status);
        pollQr(res.data.connection_id);
      } else {
        notify("error", res.data.message || "Could not start QR pairing.");
      }
    } catch (err) {
      notify("error", err.response?.data?.message || "QR pairing service unavailable.");
    } finally {
      setQrLoading(false);
    }
  };

  const pollQr = (connectionId) => {
    if (qrPollRef.current) clearInterval(qrPollRef.current);
    qrPollRef.current = setInterval(async () => {
      try {
        const res = await axiosInstance.get(`/api/whatsapp-group/qr/${connectionId}`, { params });
        if (res.data.success) {
          setQrStatus(res.data.status);
          if (res.data.qr) setQrImage(res.data.qr);
          if (res.data.status === "connected") {
            clearInterval(qrPollRef.current);
            setLastConnectionId(connectionId);
            notify("success", `Connected${res.data.wa_phone_number ? " as " + res.data.wa_phone_number : ""}. Now import your groups.`);
          } else if (["disconnected", "error"].includes(res.data.status)) {
            clearInterval(qrPollRef.current);
          }
        }
      } catch (e) {
        clearInterval(qrPollRef.current);
      }
    }, 3000);
  };

  // --- Method 2: Provider ---
  const saveProvider = async (e) => {
    e.preventDefault();
    setProviderSaving(true);
    setMessage(null);
    try {
      const res = await axiosInstance.post("/api/whatsapp-group/provider/configure", provider, { params });
      if (res.data.success) {
        setLastConnectionId(res.data.connection_id);
        notify("success", res.data.message + " Now import your groups.");
      } else {
        notify("error", res.data.message || "Failed to connect provider.");
      }
    } catch (err) {
      notify("error", err.response?.data?.message || "Failed to connect provider.");
    } finally {
      setProviderSaving(false);
    }
  };

  const importGroups = async () => {
    if (!lastConnectionId) return;
    setMessage(null);
    try {
      const res = await axiosInstance.post(
        `/api/whatsapp-group/connections/${lastConnectionId}/import-groups`, {}, { params }
      );
      if (res.data.success) {
        notify("success", res.data.message);
        setRefreshTrigger((p) => p + 1);
      } else {
        notify("error", res.data.message || "Failed to import groups.");
      }
    } catch (err) {
      notify("error", err.response?.data?.message || "Failed to import groups.");
    }
  };

  if (checkingPermissions) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress size={30} />
        <Typography sx={{ ml: 2 }}>Checking permissions...</Typography>
      </Box>
    );
  }

  if (!canView) {
    return (
      <Paper sx={{ p: 3, mt: 10, mx: "auto", maxWidth: "1200px" }}>
        <Alert severity="error">
          <Typography variant="h6">Access Denied</Typography>
          <Typography>You don't have permission to view the WhatsApp Group connector.</Typography>
        </Alert>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 3, mt: 10, mx: "auto", maxWidth: "1200px" }}>
      <Typography variant="h5" component="h1" sx={{ mb: 1 }}>
        Manage <strong>WhatsApp Group</strong>
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Bring a WhatsApp group's text into this chatbot and train it into the knowledge base.
      </Typography>
      <Divider sx={{ mb: 3 }} />

      {message && (
        <Alert severity={message.type} sx={{ mb: 2 }} onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}

      {!canModify && (
        <Alert severity="info" sx={{ mb: 2 }}>
          You have read-only access. Connecting and training require add/edit permission.
        </Alert>
      )}

      <Paper variant="outlined" sx={{ mb: 4, maxWidth: 720 }}>
        <Tabs value={tab} onChange={(e, v) => setTab(v)} variant="scrollable" sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tab label="Import Export (.txt)" />
          <Tab label="Provider API" />
          <Tab label="Live QR Pairing" />
        </Tabs>

        {/* Tab 0: Export upload */}
        {tab === 0 && (
          <Box component="form" onSubmit={handleUpload} sx={{ px: 3, py: 3 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              In WhatsApp, open the group → <em>More → Export chat → Without media</em>, then upload the .txt here.
            </Typography>
            <Stack spacing={2}>
              <Button variant="outlined" component="label" disabled={!canModify}>
                {exportFile ? exportFile.name : "Choose .txt export"}
                <input hidden type="file" accept=".txt"
                  onChange={(e) => setExportFile(e.target.files?.[0] || null)} />
              </Button>
              <TextField label="Group name (optional)" size="small" value={groupName}
                onChange={(e) => setGroupName(e.target.value)} disabled={!canModify} />
              <FormControlLabel
                control={<Checkbox checked={anonymize} onChange={(e) => setAnonymize(e.target.checked)} disabled={!canModify} />}
                label="Redact member phone numbers before training (recommended)" />
              <Box>
                <Button type="submit" variant="contained" disabled={!canModify || uploading || !exportFile}
                  sx={{ bgcolor: "#25D366" }}>
                  {uploading ? <><CircularProgress size={16} sx={{ mr: 1, color: "#fff" }} />Importing…</> : "Import & store"}
                </Button>
              </Box>
            </Stack>
          </Box>
        )}

        {/* Tab 1: Provider */}
        {tab === 1 && (
          <Box component="form" onSubmit={saveProvider} sx={{ px: 3, py: 3 }}>
            <Stack direction="row" alignItems="flex-start" spacing={0.5} sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Connect a third-party WhatsApp provider that officially supports group access.
                Credentials are encrypted at rest.
              </Typography>
              <Tooltip title="Read the Provider API integration guide" arrow>
                <IconButton size="small" color="primary" sx={{ mt: -0.5 }}
                  onClick={() => setProviderHelpOpen(true)} aria-label="Provider API integration guide">
                  <InfoOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
            <Stack spacing={2}>
              <TextField label="Provider name" size="small" value={provider.provider_type}
                onChange={(e) => setProvider({ ...provider, provider_type: e.target.value })} disabled={!canModify} />
              <TextField label="API base URL (https://…)" size="small" value={provider.api_base_url}
                onChange={(e) => setProvider({ ...provider, api_base_url: e.target.value })} required disabled={!canModify} />
              <TextField label="API key" size="small" type="password" value={provider.api_key}
                onChange={(e) => setProvider({ ...provider, api_key: e.target.value })} required disabled={!canModify} />
              <TextField label="Instance ID (optional)" size="small" value={provider.instance_id}
                onChange={(e) => setProvider({ ...provider, instance_id: e.target.value })} disabled={!canModify} />
              <Box>
                <Button type="submit" variant="contained" disabled={!canModify || providerSaving} sx={{ bgcolor: "#25D366" }}>
                  {providerSaving ? <><CircularProgress size={16} sx={{ mr: 1, color: "#fff" }} />Connecting…</> : "Connect provider"}
                </Button>
              </Box>
            </Stack>
          </Box>
        )}

        {/* Tab 2: QR */}
        {tab === 2 && (
          <Box sx={{ px: 3, py: 3 }}>
            <Alert severity="warning" sx={{ mb: 2 }}>
              Live QR pairing links a personal WhatsApp account through an unofficial WhatsApp-Web session.
              This is outside WhatsApp's official API and may violate Meta's Terms of Service. Only connect
              accounts and groups you are authorized to ingest, and ensure members consent.
            </Alert>
            <Button variant="contained" onClick={startQr} disabled={!canModify || qrLoading} sx={{ bgcolor: "#25D366" }}>
              {qrLoading ? <><CircularProgress size={16} sx={{ mr: 1, color: "#fff" }} />Starting…</> : "Generate QR code"}
            </Button>
            {qrStatus && (
              <Box sx={{ mt: 2 }}>
                <Chip label={`Status: ${qrStatus}`} color={qrStatus === "connected" ? "success" : "default"} size="small" />
              </Box>
            )}
            {qrImage && typeof qrImage === "string" && qrImage.startsWith("data:image/") && qrStatus !== "connected" && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  In WhatsApp: <em>Settings → Linked Devices → Link a Device</em>, then scan:
                </Typography>
                <img src={qrImage} alt="WhatsApp QR" style={{ width: 240, height: 240 }} />
              </Box>
            )}
          </Box>
        )}

        {lastConnectionId && (tab === 1 || tab === 2) && (
          <Box sx={{ px: 3, pb: 3 }}>
            <Divider sx={{ mb: 2 }} />
            <Button variant="outlined" onClick={importGroups} disabled={!canModify}>
              Import groups from this connection
            </Button>
          </Box>
        )}
      </Paper>

      <Dialog open={providerHelpOpen} onClose={() => setProviderHelpOpen(false)} maxWidth="md" fullWidth scroll="paper">
        <DialogTitle>Provider API — integration guide</DialogTitle>
        <DialogContent dividers>
          <Typography variant="subtitle2" gutterBottom>What this is</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Connect a third-party WhatsApp provider (a hosted gateway/BSP such as Maytapi, Whapi.cloud,
            2Chat or Periskope) that exposes a REST API for reading WhatsApp group messages. Your API key
            is stored encrypted; the chatbot pulls groups and messages from the provider on demand.
          </Typography>

          <Typography variant="subtitle2" gutterBottom>Steps</Typography>
          <Box component="ol" sx={{ pl: 3, mb: 2, "& li": { mb: 0.5 } }}>
            <li><Typography variant="body2"><b>Provider name</b> — any label (e.g. <code>maytapi</code>).</Typography></li>
            <li><Typography variant="body2"><b>API base URL</b> — your provider's REST base; must start with <code>https://</code>.</Typography></li>
            <li><Typography variant="body2"><b>API key</b> — stored Fernet-encrypted, never shown again.</Typography></li>
            <li><Typography variant="body2"><b>Instance ID</b> — optional; sent as <code>?instance_id=</code>.</Typography></li>
            <li><Typography variant="body2">Click <b>Connect provider</b>, then <b>Import groups from this connection</b>.</Typography></li>
            <li><Typography variant="body2">On each group: <b>Sync</b> to pull messages, then <b>Train</b>.</Typography></li>
          </Box>

          <Typography variant="subtitle2" gutterBottom>Required API contract</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            klonit calls these endpoints at your base URL with header <code>Authorization: Bearer &lt;api_key&gt;</code>
            {" "}(and <code>?instance_id=</code> when set):
          </Typography>
          <Box component="pre" sx={{ bgcolor: "action.hover", p: 1.5, borderRadius: 1, fontSize: 12, overflowX: "auto", mb: 2 }}>
{`GET  {base}/groups
  -> { "groups": [ { "id": "<jid>@g.us", "name": "Group name" }, ... ] }

GET  {base}/groups/{jid}/messages
  -> { "messages": [ {
         "id": "...", "text": "...",
         "sender_name": "...", "sender_number": "...",
         "timestamp": "<ISO-8601 or epoch>", "type": "text"
       }, ... ] }`}
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            The message parser is lenient — it also accepts <code>body</code>/<code>message</code> for the text,
            {" "}<code>author</code>/<code>from_name</code> for the sender name, <code>from</code>/<code>author</code> for
            the sender number, <code>message_id</code> for the id, and <code>time</code> for the timestamp.
          </Typography>

          <Alert severity="info" sx={{ mb: 2 }}>
            Each group <b>id</b> must be a WhatsApp JID ending in <code>@g.us</code>. Providers that return bare
            numeric group ids are skipped on import.
          </Alert>

          <Typography variant="subtitle2" gutterBottom>Good to know</Typography>
          <Box component="ul" sx={{ pl: 3, mb: 0, "& li": { mb: 0.5 } }}>
            <li><Typography variant="body2">Provider connections are <b>pull-based</b> (Import groups + Sync); there is no live webhook push for providers.</Typography></li>
            <li><Typography variant="body2">If your provider's real API differs from the contract above, point the base URL at a small proxy you control that exposes these two endpoints, or have your admin wire the provider's endpoints directly in the backend.</Typography></li>
            <li><Typography variant="body2">This route is ToS-cleaner than QR pairing because the provider owns the WhatsApp connection — but it still depends on the provider supporting group access.</Typography></li>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProviderHelpOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Divider sx={{ my: 3 }} />
      <Typography variant="h6" sx={{ mb: 2 }} color="textPrimary">Connected groups</Typography>
      <ListWhatsappGroups
        refreshTrigger={refreshTrigger}
        permission={{ canView, canModify, canDelete }}
        onNotify={notify}
      />
    </Paper>
  );
}
